import {useEffect, useRef, useState} from 'react';
import Button from '../../../../components/button/button';
import {createAccessKey} from '../../../../services/app/app-access-key-service';
import {Modal} from "flowbite-react";
import {HiCheck, HiOutlineClipboardCopy, HiOutlineExclamation, HiOutlineKey} from 'react-icons/hi';
import {IoIosClose} from "react-icons/io";
import {ACCESS_KEY_SCOPES, ACCESS_KEY_SCOPE_OPTIONS, EnvironmentBadge, ScopeBadge} from '../access-key-scopes';

const LABEL_CLASS = "mb-1.5 block text-sm font-medium text-gray-900";
const FIELD_CLASS = "block w-full rounded-xl border bg-white px-3.5 py-2.5 text-sm text-gray-900 placeholder-gray-400 shadow-sm transition focus:outline-none focus:ring-4";
const FIELD_OK_CLASS = "border-gray-200 hover:border-gray-300 focus:border-primary focus:ring-primary/10";
const FIELD_ERROR_CLASS = "border-rose-400 focus:border-rose-500 focus:ring-rose-500/10";

const ENVIRONMENTS = [{label: 'Test', value: 'test'}, {label: 'Live', value: 'live'}];

function AppAccessKeyCreatePage({ appId, onCompletion, onCancel }) {
    const rootRef = useRef(null);
    // flowbite's Modal re-appends its portal container on every render unless `root` is a
    // stable node, which drops input focus. Render once to anchor it (same as agent-save-modal).
    const [rootReady, setRootReady] = useState(false);
    useEffect(() => { if (rootRef.current) setRootReady(true); }, []);

    const [formData, setFormData] = useState({
        appId,
        environment: 'test',
        scope: ACCESS_KEY_SCOPES.RUNTIME,
        keyId: ''
    });
    const [keyIdError, setKeyIdError] = useState(false);
    const [formState, setFormState] = useState({ processing: false, failed: false, errorMessage: '' });
    const [accessKey, setAccessKey] = useState();
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (!copied) return;
        const timer = setTimeout(() => setCopied(false), 1500);
        return () => clearTimeout(timer);
    }, [copied]);

    const update = (name, value) => {
        setFormData(previous => ({ ...previous, [name]: value }));
        if (name === 'keyId') setKeyIdError(false);
    };

    const handleOnSubmit = async (event) => {
        event.preventDefault();
        if (formState.processing) return;
        const keyId = formData.keyId.trim();
        if (!keyId) {
            setKeyIdError(true);
            return;
        }
        setFormState({ processing: true, failed: false, errorMessage: '' });
        const finalAppId = formData.environment === 'live' ? `${appId}_live` : appId;
        const { response, status } = await createAccessKey(finalAppId, { ...formData, keyId });
        if (status === 200) {
            setFormState({ processing: false, failed: false, errorMessage: '' });
            setAccessKey(response.accessKey);
        } else {
            setFormState({ processing: false, failed: true, errorMessage: response || 'Unable to create the access key. Please try again.' });
        }
    };

    const handleClose = () => {
        if (formState.processing) return;
        // once a key exists the list must refresh, even if the dialog is dismissed
        if (accessKey) {
            onCompletion();
        } else {
            onCancel();
        }
    };

    const copyToClipboard = async () => {
        if ('clipboard' in navigator) {
            await navigator.clipboard.writeText(accessKey);
        } else {
            document.execCommand('copy', true, accessKey);
        }
        setCopied(true);
    };

    return (<div ref={rootRef}>
        <Modal show={rootReady} size="lg" popup onClose={handleClose} root={rootRef.current}
               aria-modal="true"
               aria-labelledby="create-access-key-title"
               theme={{ content: { inner: "relative flex max-h-[90dvh] flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl" } }}>
            <Modal.Body className="p-6 sm:p-8">
                <form onSubmit={handleOnSubmit} noValidate>
                    <div className="space-y-6">
                        <div className="flex items-start justify-between gap-4">
                            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary text-brand-lime dark:bg-brand-lime">
                                <HiOutlineKey className="h-6 w-6" aria-hidden="true" />
                            </span>
                            <button type="button" aria-label="Close" onClick={handleClose} disabled={formState.processing}
                                    className="rounded-lg p-1 text-gray-400 transition hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:opacity-50">
                                <IoIosClose className="h-6 w-6" />
                            </button>
                        </div>

                        <div>
                            <h3 id="create-access-key-title" className="text-xl font-semibold tracking-tight text-gray-900">
                                {accessKey ? 'Access key created' : 'Create access key'}
                            </h3>
                            <p className="mt-0.5 text-sm text-gray-500">
                                {accessKey
                                    ? 'Copy it now and store it somewhere safe.'
                                    : 'Choose what this key can do. A key is limited to one scope.'}
                            </p>
                        </div>

                        {formState.failed && (
                            <div role="alert" className="rounded-xl border border-rose-100 bg-rose-50 px-3.5 py-2.5 text-sm text-rose-700 dark:border-rose-900 dark:bg-rose-950 dark:text-rose-300">
                                {formState.errorMessage}
                            </div>
                        )}

                        {accessKey ? <>
                            <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-gray-900">{formData.keyId.trim()}</span>
                                <EnvironmentBadge environment={formData.environment} />
                                <ScopeBadge scope={formData.scope} />
                            </div>

                            <div>
                                <label htmlFor="accessKey" className={LABEL_CLASS}>Access key</label>
                                <div className="flex items-stretch gap-2">
                                    <input id="accessKey" readOnly value={accessKey} onFocus={e => e.target.select()}
                                           className={`${FIELD_CLASS} ${FIELD_OK_CLASS} font-mono text-xs`} />
                                    <button type="button" onClick={copyToClipboard}
                                            aria-label={copied ? 'Copied' : 'Copy access key'}
                                            className={`inline-flex shrink-0 items-center gap-1.5 rounded-xl border px-3.5 text-sm font-medium transition focus:outline-none focus:ring-4 ${copied
                                                ? 'border-emerald-200 bg-emerald-50 text-emerald-700 focus:ring-emerald-500/10 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-300'
                                                : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50 focus:ring-primary/10'}`}>
                                        {copied
                                            ? <HiCheck className="h-4 w-4" aria-hidden="true" />
                                            : <HiOutlineClipboardCopy className="h-4 w-4" aria-hidden="true" />}
                                        {copied ? 'Copied' : 'Copy'}
                                    </button>
                                </div>
                            </div>

                            <div role="status" className="flex gap-2.5 rounded-xl border border-amber-100 bg-amber-50 px-3.5 py-2.5 text-sm text-amber-800 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-300">
                                <HiOutlineExclamation className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                                <span>This key is shown only once. If you lose it, revoke it and create a new one.</span>
                            </div>
                        </> : <>
                            <div>
                                <label htmlFor="keyId" className={LABEL_CLASS}>Key ID</label>
                                <input
                                    type="text"
                                    name="keyId"
                                    id="keyId"
                                    autoFocus
                                    autoComplete="off"
                                    value={formData.keyId}
                                    placeholder="e.g. website-chat"
                                    onChange={e => update('keyId', e.target.value)}
                                    disabled={formState.processing}
                                    aria-invalid={keyIdError}
                                    aria-describedby={keyIdError ? 'key-id-error' : undefined}
                                    className={`${FIELD_CLASS} ${keyIdError ? FIELD_ERROR_CLASS : FIELD_OK_CLASS}`}
                                />
                                {keyIdError && (
                                    <p id="key-id-error" role="alert" className="mt-1.5 text-xs text-rose-600 dark:text-rose-400">Please enter a key ID.</p>
                                )}
                            </div>

                            <div>
                                <span id="environment-label" className={LABEL_CLASS}>Environment</span>
                                <div role="radiogroup" aria-labelledby="environment-label"
                                     className="inline-flex rounded-xl border border-gray-200 bg-gray-50 p-1">
                                    {ENVIRONMENTS.map(env => {
                                        const selected = formData.environment === env.value;
                                        return (
                                            <button key={env.value} type="button" role="radio" aria-checked={selected}
                                                    disabled={formState.processing}
                                                    onClick={() => update('environment', env.value)}
                                                    className={`rounded-lg px-4 py-1.5 text-sm font-medium transition focus:outline-none focus:ring-2 focus:ring-primary/20 ${selected
                                                        ? 'bg-white text-gray-900 shadow-sm'
                                                        : 'text-gray-500 hover:text-gray-900'}`}>
                                                {env.label}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            <div>
                                <span id="scope-label" className={LABEL_CLASS}>Scope</span>
                                <div role="radiogroup" aria-labelledby="scope-label" className="grid gap-3 sm:grid-cols-2">
                                    {ACCESS_KEY_SCOPE_OPTIONS.map(({ value, label, description, icon: Icon }) => {
                                        const selected = formData.scope === value;
                                        return (
                                            <button key={value} type="button" role="radio" aria-checked={selected}
                                                    disabled={formState.processing}
                                                    onClick={() => update('scope', value)}
                                                    className={`relative flex flex-col items-start gap-2 rounded-xl border p-4 text-left transition focus:outline-none focus:ring-4 focus:ring-primary/10 ${selected
                                                        ? 'border-primary bg-gray-50 ring-1 ring-primary dark:border-brand-lime dark:ring-brand-lime'
                                                        : 'border-gray-200 bg-white hover:border-gray-300'}`}>
                                                <span className={`flex h-9 w-9 items-center justify-center rounded-lg ${selected
                                                    ? 'bg-primary text-brand-lime dark:bg-brand-lime'
                                                    : 'bg-gray-100 text-gray-600'}`}>
                                                    <Icon className="h-5 w-5" aria-hidden="true" />
                                                </span>
                                                <span className="text-sm font-semibold text-gray-900">{label}</span>
                                                <span className="text-xs leading-relaxed text-gray-500">{description}</span>
                                                {selected && (
                                                    <span className="absolute right-3 top-3 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-white dark:bg-brand-lime">
                                                        <HiCheck className="h-3.5 w-3.5" aria-hidden="true" />
                                                    </span>
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        </>}

                        <div className="flex flex-row justify-end gap-2 border-t border-gray-100 pt-5">
                            {accessKey
                                ? <Button type="button" text="Done" onClick={onCompletion} />
                                : <>
                                    <Button type="button" color="default" text="Cancel" onClick={handleClose} disabled={formState.processing} />
                                    {formState.processing
                                        ? <Button type="button" text="Create key" loading={true} disabled={true} />
                                        : <Button type="submit" text="Create key" />}
                                </>}
                        </div>
                    </div>
                </form>
            </Modal.Body>
        </Modal>
    </div>)
}

export default AppAccessKeyCreatePage;
